import {useState} from "react";
import {Navigate, useNavigate, useLocation} from "react-router-dom";
import {Formik, Form, Field, ErrorMessage} from "formik";
import * as Yup from "yup";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import bgImage from "assets/images/ai-background.jpg";
import BasicLayout from "../components/BasicLayout";
import api from "../../../API/api";
import {toast} from "sonner";
import { useAuth } from "../../../context/authContext";

const validationSchema = Yup.object().shape({
  username: Yup.string().required("User name is required"),
  password: Yup.string()
    // .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

function Basic() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isEmailVerified, login } = useAuth();
  
  // If user is already authenticated and email verified, redirect to dashboard or intended page
  if (isAuthenticated && isEmailVerified) {
    const from = location.state?.from?.pathname || "/dashboard";
    return <Navigate to={from} replace />;
  }

  const handleLogin = async (values, {setSubmitting}) => {
    setSubmitting(true);
    try {
      let redirectUrl = localStorage.getItem("reportUrl");
      const response = await api.post("/login", values);
      if (response?.data?.data) {
        const userData = response?.data?.data;
        
        // Use the auth context to login
        login(userData);
        
        toast.success(response?.data?.message);
        
        if (userData.is_verified === true) {
          if (redirectUrl) {
            navigate(redirectUrl);
          } else {
            const from = location.state?.from?.pathname || "/dashboard";
            navigate(from);
          }
        } else {
          navigate("/verify-email");
        }
      }
    } catch (error) {
      console.error("Login failed:", error.response?.data?.message);
      if (error?.message?.includes("Network Error")) {
        toast.error("Network Error");
      } else {
        toast.error(error.response?.data?.message || "Login failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign in
          </MDTypography>
          <Grid
            container
            spacing={3}
            justifyContent="center"
            sx={{mt: 1, mb: 2}}
          ></Grid>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <Formik
            initialValues={{username: "", password: ""}}
            validationSchema={validationSchema}
            onSubmit={handleLogin}
          >
            {({isSubmitting}) => (
              <Form>
                <MDBox mb={2}>
                  <Field name="username">
                    {({field}) => (
                      <MDInput
                        {...field}
                        type="text"
                        fullWidth
                        as={MDInput}
                        autoComplete="off"
                        placeholder="User Name"
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="username"
                    component="div"
                    style={{
                      color: "red",
                      fontSize: "11px",
                      marginLeft: "4px",
                    }}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <Field name="password">
                    {({field, meta}) => (
                      <>
                        <MDInput
                          {...field}
                          fullWidth
                          as={MDInput}
                          type="password"
                          autoComplete="off"
                          placeholder="Passowrd"
                        />
                        {meta.touched && meta.error && (
                          <div
                            style={{
                              color: "red",
                              fontSize: "11px",
                              marginLeft: "4px",
                            }}
                          >
                            {meta.error}
                          </div>
                        )}
                      </>
                    )}
                  </Field>
                </MDBox>
                {/* <MDBox display="flex" alignItems="center" ml={-1}>
                  <Switch checked={rememberMe} onChange={handleSetRememberMe} />
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    onClick={handleSetRememberMe}
                    sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                  >
                    &nbsp;&nbsp;Remember me
                  </MDTypography>
                </MDBox> */}

                <MDBox mt={4} mb={1}>
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="info"
                    fullWidth
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Signing in..." : "Sign in"}
                  </MDButton>
                </MDBox>
                <MDBox mt={3} mb={1} textAlign="center">
                  <MDTypography
                    variant="button"
                    color="text"
                    sx={{cursor: "pointer", fontWeight: "400"}}
                    onClick={() => navigate("/forgot-password")}
                  >
                    Forgot Password !!
                  </MDTypography>
                </MDBox>
              </Form>
            )}
          </Formik>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
